import _extends from 'babel-runtime/helpers/extends';
import _slicedToArray from 'babel-runtime/helpers/slicedToArray';
import _Object$entries from 'babel-runtime/core-js/object/entries';
import _defineProperty from 'babel-runtime/helpers/defineProperty';
import _toConsumableArray from 'babel-runtime/helpers/toConsumableArray';
import _Object$getPrototypeOf from 'babel-runtime/core-js/object/get-prototype-of';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
/* eslint-disable complexity, no-underscore-dangle */
import cx from 'classnames';
import debounce from 'lodash/debounce';
import kebabCase from 'lodash/kebabCase';
import memoize from 'lodash/memoize';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import { category, makeOutfitParser } from '../outfit-parser';
import outfitPropValidator from '../outfitPropValidator';
import Portal from '../Portal';
import * as styles from './Dropdown.style';

var POSITION = {
  TOP: 'TOP',
  RIGHT: 'RIGHT',
  BOTTOM: 'BOTTOM',
  LEFT: 'LEFT'
};

var DIRECTIONS_Y = {
  top: POSITION.TOP,
  bottom: POSITION.BOTTOM
};
var DIRECTIONS_X = {
  left: POSITION.LEFT,
  right: POSITION.RIGHT
};

export var KEYCODES = {
  ESCAPE: 27
};

var MUTATION_OBSERVER_CONFIG = {
  attributes: true,
  childList: true,
  characterData: true,
  subtree: true
};

var chooseVerticalSide = function chooseVerticalSide(_ref) {
  var spaceTop = _ref.spaceTop,
      spaceBottom = _ref.spaceBottom,
      contentHeight = _ref.contentHeight;

  if (spaceBottom >= contentHeight) return POSITION.BOTTOM;
  if (spaceTop >= contentHeight) return POSITION.TOP;
  return spaceBottom >= spaceTop ? POSITION.BOTTOM : POSITION.TOP;
};

var chooseHorizontalSide = function chooseHorizontalSide(_ref2) {
  var spaceLeftFromRightEdge = _ref2.spaceLeftFromRightEdge,
      spaceRightFromLeftEdge = _ref2.spaceRightFromLeftEdge,
      contentWidth = _ref2.contentWidth;

  if (spaceRightFromLeftEdge >= contentWidth) return POSITION.RIGHT;
  if (spaceLeftFromRightEdge >= contentWidth) return POSITION.LEFT;
  return spaceRightFromLeftEdge >= spaceLeftFromRightEdge ? POSITION.RIGHT : POSITION.LEFT;
};

var chooseCorner = function chooseCorner(_ref3) {
  var spaceTop = _ref3.spaceTop,
      spaceBottom = _ref3.spaceBottom,
      spaceLeftFromRightEdge = _ref3.spaceLeftFromRightEdge,
      spaceRightFromLeftEdge = _ref3.spaceRightFromLeftEdge,
      contentWidth = _ref3.contentWidth,
      contentHeight = _ref3.contentHeight,
      directionX = _ref3.directionX,
      directionY = _ref3.directionY;

  var positionHorizontal = DIRECTIONS_X[directionX];
  var positionVertical = DIRECTIONS_Y[directionY];
  var vertical = positionVertical || chooseVerticalSide({ spaceTop: spaceTop, spaceBottom: spaceBottom, contentHeight: contentHeight });
  var horizontal = positionHorizontal || chooseHorizontalSide({ spaceLeftFromRightEdge: spaceLeftFromRightEdge, spaceRightFromLeftEdge: spaceRightFromLeftEdge, contentWidth: contentWidth });

  return {
    vertical: vertical,
    horizontal: horizontal === POSITION.LEFT ? POSITION.RIGHT : POSITION.LEFT
  };
};

var recognizeSurrounding = function recognizeSurrounding(_ref4) {
  var left = _ref4.left,
      top = _ref4.top,
      width = _ref4.width,
      height = _ref4.height;
  var _document$documentEle = document.documentElement,
      viewportWidth = _document$documentEle.clientWidth,
      viewportHeight = _document$documentEle.clientHeight;


  return {
    spaceTop: top,
    spaceBottom: viewportHeight - (top + height),
    spaceLeft: left,
    spaceLeftFromRightEdge: left + width,
    spaceRightFromLeftEdge: viewportWidth - left
  };
};

var px = function px(value) {
  return value + 'px';
};
var calc100PercentMinus = function calc100PercentMinus(value) {
  return 'calc(100% - ' + value + ')';
};
/*
 * Based on: https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
 */
var isIE11 = memoize(function () {
  return !!document.documentMode;
});

var updateContentSize = debounce(function (container, node) {
  var dimensions = container.getBoundingClientRect();
  node.style.setProperty('width', dimensions.width + 'px');
  node.style.setProperty('height', dimensions.height + 'px');
}, 200);

var producePositionStyles = function producePositionStyles(_ref5) {
  var corner = _ref5.corner,
      triggerSurrounding = _ref5.triggerSurrounding,
      triggerDimensions = _ref5.triggerDimensions,
      shrink = _ref5.shrink,
      grow = _ref5.grow,
      fixed = _ref5.fixed;

  var style = {};

  var fixedScrollCoordinates = { x: 0, y: 0 };
  var normalScrollCoordinates = {
    x: window.pageXOffset || document.documentElement.scrollLeft || 0,
    y: window.pageYOffset || document.documentElement.scrollTop || 0
  };

  var scroll = fixed ? fixedScrollCoordinates : normalScrollCoordinates;

  if (corner.vertical === POSITION.TOP) {
    style.bottom = calc100PercentMinus(px(triggerSurrounding.spaceTop + scroll.y));
    style.maxHeight = px(triggerSurrounding.spaceTop);
    style.top = 'auto';
  } else if (corner.vertical === POSITION.BOTTOM) {
    var spaceAboveBottomEdge = triggerSurrounding.spaceTop + triggerDimensions.height;
    style.top = px(spaceAboveBottomEdge + scroll.y);
    if (triggerSurrounding.spaceBottom >= 0) {
      style.maxHeight = calc100PercentMinus(px(triggerDimensions.y));
    }
  }

  if (corner.horizontal === POSITION.RIGHT) {
    style.right = calc100PercentMinus(px(triggerSurrounding.spaceLeftFromRightEdge + scroll.x));
    style.maxWidth = px(triggerSurrounding.spaceLeftFromRightEdge);
    style.left = 'auto';
  } else if (corner.horizontal === POSITION.LEFT) {
    style.left = px(Math.max(triggerSurrounding.spaceLeft + scroll.x, 0));
    style.maxWidth = calc100PercentMinus(px(triggerSurrounding.spaceLeft));
    style.right = 'auto';
  }

  if (shrink) {
    style.maxWidth = px(triggerDimensions.width);
  }
  if (grow) {
    style.minWidth = px(triggerDimensions.width);
  }

  return style;
};

var dimensionsList = ['fixed', 'grow', 'shrink'];
var directionsXList = ['left', 'right'];
var directionsYList = ['top', 'bottom'];
var parseOutfit = makeOutfitParser(category('dimensions', dimensionsList, true), category('directionX', directionsXList), category('directionY', directionsYList));

var Dropdown = function (_PureComponent) {
  _inherits(Dropdown, _PureComponent);

  function Dropdown() {
    var _ref6;

    var _temp, _this, _ret;

    _classCallCheck(this, Dropdown);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn(this, (_ref6 = Dropdown.__proto__ || _Object$getPrototypeOf(Dropdown)).call.apply(_ref6, [this].concat(args))), _this), _initialiseProps.call(_this), _temp), _possibleConstructorReturn(_this, _ret);
  }

  _createClass(Dropdown, [{
    key: 'componentDidMount',
    value: function componentDidMount() {
      this.mutationObserver = new MutationObserver(this.calculateStyles);
      if (this.open && this.sliderDOMNode) this.prepareDropdown();
    }
  }, {
    key: 'componentWillReceiveProps',
    value: function componentWillReceiveProps(_ref7) {
      var open = _ref7.open;

      if (open !== undefined && open !== this.open) this.open = open;
    }
  }, {
    key: 'componentDidUpdate',
    value: function componentDidUpdate() {
      if (this.open) {
        this.prepareDropdown();
      } else {
        this.clearDropdown();
      }
    }
  }, {
    key: 'componentWillUnmount',
    value: function componentWillUnmount() {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
      this.eventListenersArgs.forEach(function (args) {
        var _window;

        return (_window = window).removeEventListener.apply(_window, _toConsumableArray(args));
      });
    }
  }, {
    key: 'render',
    value: function render() {
      var _cx;

      var _props = this.props,
          children = _props.children,
          className = _props.className,
          outfit = _props.outfit,
          zIndex = _props.zIndex;

      var fixed = parseOutfit(outfit).get('dimensions').has('fixed');
      var classNames = cx((_cx = {}, _defineProperty(_cx, styles.root, true), _defineProperty(_cx, styles.rootFixed, fixed), _cx), className);

      return this.open ? React.createElement(
        Portal,
        null,
        React.createElement(
          'div',
          {
            'data-test-id': 'Dropdown',
            style: { zIndex: zIndex },
            ref: this.storeContainerDomNode,
            className: classNames
          },
          React.createElement(
            'div',
            {
              'data-test-id': 'Dropdown__slider',
              ref: this.storeSliderDomNode,
              className: styles.slider
            },
            React.createElement(
              'div',
              { ref: this.storeContentDomNode, className: styles.content },
              children
            )
          )
        )
      ) : null;
    }
  }, {
    key: 'open',
    get: function get() {
      return this._open;
    },
    set: function set(open) {
      this._open = open;
      if (!open && this.sliderDOMNode && this.contentDOMNode && !isEmpty(this.corner)) {
        this.sliderDOMNode.addEventListener(this.whichAnimationEvent(), this.unmount);

        var pos = this.corner.vertical.toLowerCase();
        var animation = void 0;
        if (pos === 'top') {
          animation = styles.animationSlideOutTop;
        }
        if (pos === 'bottom') {
          animation = styles.animationSlideOutBottom;
        }
        this.sliderDOMNode.style.setProperty('animation-name', animation);
      } else {
        this.forceUpdate();
      }
    }
  }]);

  return Dropdown;
}(PureComponent);

Dropdown.propTypes = {
  /**
   * Type: **string**.
   *
   * @see See [Commonly used attributes](#components-common-props) for a detailed description.
   *
   * Options:
   *
   *    - Dimensions: (multi choice)
   *      - "**fixed**" - dropdown should be fixed (*Default:* absolute positioned),
   *      - "**grow**" - set minWidth of dropdown to width of the trigger,
   *      - "**shrink**" - set maxWidth of dropdown to width of the trigger,
   *
   *    - DirectionY:
   *      - "**top**",
   *      - "**bottom**",
   *
   *      Vertical direction in which dropdown should be opened.
   *
   *      *Default:* detected automatically with preference to "bottom"
   *      (if there is enough space on the current view).
   *
   *    - DirectionX:
   *      - "**left**",
   *      - "**right**"
   *
   *      Horizontal direction in which dropdown should be opened.
   *
   *      *Default:* detected automatically with preference to "right"
   *      (if there is enough space on the current view).
   */
  outfit: outfitPropValidator([].concat(dimensionsList, directionsXList, directionsYList)),

  /**
   * Type: **function** or **object**
   *
   * Reference to trigger or function that returns reference
   */
  stickTo: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,

  /** Content of dropdown */
  children: PropTypes.node,

  /**
   * Type: **function**.
   *
   * Callback fired when dropdown should be closed
   */
  onClose: PropTypes.func,

  /**
   * Type: **bool**.
   *
   * Decide whether dropdown should be open or closed
   */
  open: PropTypes.bool,

  /**
   * Type: **bool**.
   *
   * Decide whether dropdown should be open by default */
  defaultOpen: PropTypes.bool,

  /**
   * Type: **string**.
   *
   * @see See [Commonly used attributes](#components-common-props) for a detailed description.
   */
  className: PropTypes.string,

  /** zIndex passed to styles */
  zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

var _initialiseProps = function _initialiseProps() {
  var _this2 = this;

  this._open = this.props.open === undefined ? Boolean(this.props.defaultOpen) : this.props.open;
  this.containerDOMNode = null;
  this.sliderDOMNode = null;
  this.contentDOMNode = null;
  this.triggerDOMNode = null;
  this.corner = {};
  this.mutationObserver = null;

  this.prepareDropdown = function () {
    _this2.eventListenersArgs.forEach(function (args) {
      var _window2;

      return (_window2 = window).addEventListener.apply(_window2, _toConsumableArray(args));
    });
    if (typeof _this2.props.stickTo === 'function') {
      _this2.triggerDOMNode = _this2.props.stickTo();
    } else {
      _this2.triggerDOMNode = _this2.props.stickTo;
    }
    _this2.mutationObserver.disconnect();
    if (_this2.contentDOMNode) {
      _this2.mutationObserver.observe(findDOMNode(_this2.contentDOMNode), // eslint-disable-line react/no-find-dom-node
      MUTATION_OBSERVER_CONFIG);
    }
    _this2.calculateStyles();
  };

  this.clearDropdown = function () {
    _this2.mutationObserver.disconnect();
    _this2.eventListenersArgs.forEach(function (args) {
      var _window3;

      return (_window3 = window).removeEventListener.apply(_window3, _toConsumableArray(args));
    });
  };

  this.unmount = function () {
    _this2.sliderDOMNode.removeEventListener(_this2.whichAnimationEvent(), _this2.unmount);
    _this2.corner = {};
    _this2.forceUpdate();
  };

  this.whichAnimationEvent = function () {
    var animation = 'animationend';
    var el = document.createElement('fakeelement');
    var animations = {
      animation: 'animationend',
      OAnimation: 'oAnimationEnd',
      MozAnimation: 'animationend',
      WebkitAnimation: 'webkitAnimationEnd'
    };

    _Object$entries(animations).forEach(function (_ref8) {
      var _ref9 = _slicedToArray(_ref8, 2),
          key = _ref9[0],
          value = _ref9[1];

      if (el.style[key] !== undefined) {
        animation = value;
      }
    });
    return animation;
  };

  this.handleOutsideClick = function (e) {
    if (!document.documentElement.contains(e.target) || _this2.triggerDOMNode.contains(e.target) || _this2.containerDOMNode.contains(e.target)) return;
    if (_this2.props.onClose) _this2.props.onClose(e);else if (_this2.props.open === undefined) _this2.open = false;
  };

  this.handleKeydown = function (e) {
    if (e.keyCode === KEYCODES.ESCAPE) {
      if (_this2.props.onClose) _this2.props.onClose(e);else if (_this2.props.open === undefined) _this2.open = false;
    }
  };

  this.calculateStyles = function () {
    if (_this2.triggerDOMNode && _this2.containerDOMNode && _this2.sliderDOMNode && _this2.contentDOMNode) {
      var triggerDimensions = _this2.triggerDOMNode.getBoundingClientRect();
      var triggerSurrounding = recognizeSurrounding(triggerDimensions);

      var _contentDOMNode = _this2.contentDOMNode,
          scrollWidth = _contentDOMNode.scrollWidth,
          scrollHeight = _contentDOMNode.scrollHeight;
      var outfit = _this2.props.outfit;


      var parsedOutfit = parseOutfit(outfit);

      var _parsedOutfit$get = parsedOutfit.get('directionX'),
          _parsedOutfit$get2 = _slicedToArray(_parsedOutfit$get, 1),
          directionX = _parsedOutfit$get2[0];

      var _parsedOutfit$get3 = parsedOutfit.get('directionY'),
          _parsedOutfit$get4 = _slicedToArray(_parsedOutfit$get3, 1),
          directionY = _parsedOutfit$get4[0];

      var corner = chooseCorner(_extends({}, triggerSurrounding, {
        contentWidth: scrollWidth,
        contentHeight: scrollHeight,
        directionX: directionX,
        directionY: directionY
      }));
      var dimensions = parsedOutfit.get('dimensions');
      var grow = dimensions.has('grow');
      var shrink = dimensions.has('shrink');
      var fixed = dimensions.has('fixed');

      var style = producePositionStyles({
        corner: corner,
        triggerSurrounding: triggerSurrounding,
        triggerDimensions: triggerDimensions,
        shrink: shrink,
        grow: grow,
        fixed: fixed
      });

      _Object$entries(style).forEach(function (_ref10) {
        var _ref11 = _slicedToArray(_ref10, 2),
            key = _ref11[0],
            value = _ref11[1];

        return _this2.containerDOMNode.style.setProperty(kebabCase(key), value);
      });

      var pos = corner.vertical.toLowerCase();
      var animation = void 0;
      if (pos === 'top') {
        animation = styles.animationSlideInTop;
      }
      if (pos === 'bottom') {
        animation = styles.animationSlideInBottom;
      }
      _this2.sliderDOMNode.style.setProperty('animation-name', animation);

      _this2.corner = corner;

      if (isIE11()) {
        _this2.sliderDOMNode.style.setProperty('width', 'auto');
        _this2.sliderDOMNode.style.setProperty('height', 'auto');

        var CONTAINER_SIZE_APPLICATION_DELAY = 300;
        setTimeout(updateContentSize, CONTAINER_SIZE_APPLICATION_DELAY, _this2.containerDOMNode, _this2.sliderDOMNode);
      }
    }
  };

  this.eventListenersArgs = [['resize', this.calculateStyles], ['click', this.handleOutsideClick, { capture: true }], ['touchstart', this.handleOutsideClick, { capture: true, passive: false }], ['keydown', this.handleKeydown, { capture: true }]];

  this.storeContainerDomNode = function (node) {
    _this2.containerDOMNode = node;
  };

  this.storeSliderDomNode = function (node) {
    _this2.sliderDOMNode = node;
  };

  this.storeContentDomNode = function (node) {
    _this2.contentDOMNode = node;
  };
};

export default Dropdown;